import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Alert, Spinner } from '../shared/components/feedback';
import { PageHeader } from '../shared/components/PageHeader';
import { Switch } from '../shared/components/Switch';
import { Button } from '../shared/components/Button';
import { ApiError } from '../api/client';
import * as userProfileApi from '../api/userProfile';
import { AiUsagePanel } from './AiUsagePanel';
import { DataDeletionRequestsPanel } from './DataDeletionRequestsPanel';

export default function SettingsPage() {
  const [mailNotificationEnabled, setMailNotificationEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [message, setMessage] = useState<{
    tone: 'success' | 'danger';
    text: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    userProfileApi
      .getMyProfile()
      .then((data) => {
        if (cancelled) return;
        setMailNotificationEnabled(!!data.mailNotificationEnabled);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const handleToggleMailNotification = async (next: boolean) => {
    setSaving(true);
    setMessage(null);
    setMailNotificationEnabled(next);
    try {
      const data = await userProfileApi.updateMailNotification(next);
      setMailNotificationEnabled(!!data.mailNotificationEnabled);
      setMessage({
        tone: 'success',
        text: next
          ? '글 발행 메일 알림을 켰습니다.'
          : '글 발행 메일 알림을 껐습니다.',
      });
    } catch (err) {
      setMailNotificationEnabled(!next);
      setMessage({
        tone: 'danger',
        text:
          err instanceof ApiError
            ? err.message
            : '알림 설정을 저장하지 못했습니다.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title="설정"
        description="발행 알림, AI 사용량과 내 데이터 관리 설정을 확인하세요."
      />

      <Card>
        <CardTitle>알림</CardTitle>
        <CardDescription>새 글의 발행 소식을 이메일로 받아보세요.</CardDescription>
        {message && <Alert tone={message.tone}>{message.text}</Alert>}
        {loading ? (
          <Center role="status">
            <Spinner $size={22} aria-hidden="true" />
            알림 설정을 불러오는 중입니다.
          </Center>
        ) : loadError ? (
          <ErrorState>
            <Alert tone="danger">알림 설정을 불러오지 못했습니다. 다시 시도해 주세요.</Alert>
            <Button $variant="secondary" onClick={() => setRefresh((value) => value + 1)}>다시 불러오기</Button>
          </ErrorState>
        ) : (
          <ToggleRow>
            <div>
              <ToggleLabel>글 발행 메일 알림</ToggleLabel>
              <ToggleHelp>
                {saving ? '변경 사항을 저장하고 있습니다…' : mailNotificationEnabled ? '새 글이 발행되면 가입 이메일로 알림을 보냅니다.' : '현재 이메일 알림이 꺼져 있습니다.'}
              </ToggleHelp>
            </div>
            <Switch
              checked={mailNotificationEnabled}
              disabled={saving}
              aria-label="글 발행 메일 알림"
              onChange={(next) => void handleToggleMailNotification(next)}
            />
          </ToggleRow>
        )}
      </Card>

      <Card><AiUsagePanel /></Card>
      <Card><DataDeletionRequestsPanel /></Card>

      <Card>
        <CardTitle>법적 고지</CardTitle>
        <CardDescription>개인정보 처리 기준과 데이터 삭제 방법을 확인하세요.</CardDescription>
        <LegalList>
          <LegalRow to="/privacy">
            <div>
              <LegalLabel>개인정보 처리방침</LegalLabel>
              <ToggleHelp>
                수집·이용 목적과 보유 기간 등 개인정보 처리 기준을 확인합니다.
              </ToggleHelp>
            </div>
            <LegalChevron aria-hidden>→</LegalChevron>
          </LegalRow>
          <LegalRow to="/data-deletion">
            <div>
              <LegalLabel>사용자 데이터 삭제</LegalLabel>
              <ToggleHelp>
                계정 및 서비스에 저장된 개인 데이터의 삭제를 요청합니다.
              </ToggleHelp>
            </div>
            <LegalChevron aria-hidden>→</LegalChevron>
          </LegalRow>
        </LegalList>
      </Card>
    </Page>
  );
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  min-width: 0;
`;

const Card = styled.section`
  padding: clamp(18px, 3vw, 28px);
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 6px;
`;

const CardDescription = styled.p`
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  min-width: 0;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

const ToggleHelp = styled.p`
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const LegalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LegalRow = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  color: inherit;
  min-width: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
`;

const LegalLabel = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 600;
`;

const LegalChevron = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 16px;
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 14px;
  padding: 24px;
  color: ${({ theme }) => theme.colors.primary};
`;
